/**
 * @fileoverview Generates a change log from git commit history using an LLM. The file defines the `CommitInfo` interface, `getCommitRange` function to retrieve commits, `generateChangeLogContent` to generate the change log text, and the main `generateChangeLog` function. It uses `@iterator/shared-node` for LLM types and usage tracking.
 */

import { Model } from '@iterator/shared-node/ai/llm_types'
import { trackUsage } from '@iterator/shared-node/util/usage_tracker'
import { exec as execCallback } from 'child_process'
import { promisify } from 'util'

const exec = promisify(execCallback)

interface CommitInfo {
  hash: string
  author: string
  date: string
  message: string
}

async function getCommitRange(directory: string, from: string, to: string): Promise<CommitInfo[]> {
  try {
    // Get git log between two commits/tags/branches
    const { stdout } = await exec(
      `git log ${from}..${to} --pretty=format:"%H|%an|%ad|%s%n%b" --date=short`,
      { cwd: directory },
    )

    if (!stdout.trim()) {
      return []
    }

    // Parse the git log output
    const commits: CommitInfo[] = []
    const rawCommits = stdout.trim().split('\n\n')

    for (const rawCommit of rawCommits) {
      const lines = rawCommit.split('\n')
      const parts = lines[0]?.split('|')
      if (!parts || parts.length < 4) continue

      const [hash, author, date, ...messageParts] = parts
      const message = messageParts.join('|') + (lines.slice(1).join('\n') || '')

      commits.push({
        hash: hash?.trim() || '',
        author: author?.trim() || '',
        date: date?.trim() || '',
        message: message.trim(),
      })
    }

    return commits
  } catch (error) {
    throw new Error(`Failed to get commit range: ${error}`)
  }
}

async function generateChangeLogContent(
  model: Model,
  commits: CommitInfo[],
  format: 'markdown' | 'text' | 'json',
): Promise<string> {
  if (commits.length === 0) {
    return 'No commits found in the specified range.'
  }

  const commitMessages = commits.map((c) => c.message).join('\n---\n')

  const system = `You are a change log generator. Analyze git commit messages and generate a concise, well-organized change log.

Group related changes together and create bullet points for key changes.
Focus on what changed from a user/feature perspective, not implementation details.
Ignore minor commits like typos, formatting, or small refactors unless they're significant.
If commits mention issues or PRs, include those references.

Output format: ${format}

For markdown format, use:
- No need to provide sections or headers, just a bullet point list of changes
- Each change should be a single short sentence (<20 words)

For text format, use plain bullet points without markdown formatting.

For json format, return an object with sections as keys and arrays of changes as values.`

  const userMessage = `Generate a change log for these commit messages:\n\n${commitMessages}`

  // Call the model directly without animations
  let result = ''
  const response = await model
    .getApi()
    .streamChatCompletions(system, [], [{ role: 'user', content: userMessage }], (chunk) => {
      result += chunk
    })

  trackUsage('generateChangeLog', response.usage, model)

  return result
}

export async function generateChangeLog(
  directory: string,
  model: Model,
  from: string,
  to: string,
  format: 'markdown' | 'text' | 'json',
): Promise<void> {
  try {
    // If from is not specified, try to get the last tag
    let fromRef = from
    if (!fromRef) {
      try {
        const { stdout } = await exec('git describe --tags --abbrev=0', { cwd: directory })
        fromRef = stdout.trim()
      } catch {
        // If no tags exist, use the first commit
        const { stdout } = await exec('git rev-list --max-parents=0 HEAD', { cwd: directory })
        fromRef = stdout.trim().split('\n')[0] || 'HEAD~10'
      }
    }

    // Get commits in the range
    const commits = await getCommitRange(directory, fromRef, to)

    if (commits.length === 0) {
      console.log(`No commits found between ${fromRef} and ${to}`)
      return
    }

    // Generate the change log
    const changeLog = await generateChangeLogContent(model, commits, format)

    // Output only the change log content
    console.log(changeLog)
  } catch (err) {
    console.error(`Failed to generate change log: ${err}`)
    throw err
  }
}
